import { Group } from "~/types/group";

export const createGroup = async (group: Omit<Group, "id" | "createdAt" | "updatedAt">): Promise<Group> => {
  const response = await fetch("/api/groups", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      ...group,
      status: "active" // New groups are always active
    })
  });

  if (!response.ok) {
    throw new Error("Failed to create group");
  }

  return response.json();
};

export const updateGroup = async (group: Group): Promise<Group> => {
  const response = await fetch(`/api/groups/${group.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(group)
  });

  if (!response.ok) {
    throw new Error("Failed to update group");
  }

  return response.json();
};

/**
 * Delete a group by its ID
 * @param groupId The ID of the group to delete
 * @returns Promise resolving to a success status
 */
export const deleteGroup = async (groupId: number | string): Promise<{ success: boolean; message?: string }> => {
  try {
    // Ensure we're using a numeric ID
    const numericId = typeof groupId === "string" ? parseInt(groupId, 10) : groupId;

    // Log the request details
    console.log(`Attempting to delete group with ID: ${numericId} (original: ${groupId}, type: ${typeof groupId})`);

    if (isNaN(numericId)) {
      return {
        success: false,
        message: "Invalid group ID"
      };
    }

    // Try to use the POST method with _method=DELETE as a workaround for potential DELETE method issues
    const response = await fetch(`/api/groups/${numericId}`, {
      method: "POST", // Use POST instead of DELETE as a test
      headers: {
        "Content-Type": "application/json",
        "X-HTTP-Method-Override": "DELETE" // Signal the server we want DELETE
      },
      body: JSON.stringify({ _method: "DELETE" }), // Additional signal
      credentials: "include" // Include cookies for authentication
    });

    console.log(`Delete response status: ${response.status}`);

    if (!response.ok) {
      // Try to get detailed error information
      let errorMessage = `Failed to delete group (${response.status})`;
      try {
        const errorData = await response.json();
        console.error("Error response data:", errorData);
        errorMessage = errorData.error || errorMessage;
      } catch (parseError) {
        console.error("Could not parse error response:", parseError);
      }

      // As a fallback, let's try the standard DELETE method
      console.log("Trying standard DELETE method as fallback");

      try {
        const deleteResponse = await fetch(`/api/groups/${numericId}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json"
          },
          credentials: "include"
        });

        console.log(`Standard DELETE response: ${deleteResponse.status}`);

        if (deleteResponse.ok) {
          return {
            success: true,
            message: "Group successfully deleted (via standard DELETE)"
          };
        }
      } catch (deleteError) {
        console.error("Error with standard DELETE:", deleteError);
      }

      return {
        success: false,
        message: errorMessage
      };
    }

    return {
      success: true,
      message: "Group successfully deleted"
    };
  } catch (error) {
    console.error("Error deleting group:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "An unexpected error occurred"
    };
  }
};
